import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getProduct } from '../api';
import IframeModal from '../components/IframeModal';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await getProduct(id);
        setProduct(data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Product not found</div>
      </div>
    );
  }

  const allImages = product.images && product.images.length > 0 ? product.images : [
    'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=800&q=80'
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Kembali
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold">{product.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="mb-4 rounded-lg overflow-hidden">
              <img
                src={allImages[selectedImage]}
                alt={product.title}
                className="w-full h-96 object-cover"
              />
            </div>
            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`rounded-lg overflow-hidden border-2 ${
                      selectedImage === idx ? 'border-orange-500' : 'border-gray-700'
                    }`}
                  >
                    <img src={img} alt={`${product.title} ${idx + 1}`} className="w-full h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Video */}
            {product.videos && product.videos.length > 0 && (
              <div className="mt-6 rounded-lg overflow-hidden">
                <video
                  src={product.videos[0]}
                  controls
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-2xl font-bold text-orange-500">{product.date}</span>
                <span className="text-xl">{product.time}</span>
              </div>
              <div className="text-xl mb-2">
                <strong>{product.location}</strong>
              </div>
              <div className="text-lg text-gray-400 mb-4">{product.venue}</div>
              
              {product.status === 'sold-out' ? (
                <div className="inline-block px-6 py-3 bg-gray-800 text-white rounded-full text-lg font-semibold">
                  SOLD OUT
                </div>
              ) : (
                <div className="text-3xl font-bold text-orange-500 mb-6">{product.price}</div>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Deskripsi</h2>
              <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{product.description}</p>
            </div>

            {product.status !== 'sold-out' && product.external_link && (
              <Button
                onClick={() => setShowIframe(true)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-6"
              >
                Beli Sekarang
                <ExternalLink className="ml-2" size={20} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Iframe Modal */}
      <IframeModal
        isOpen={showIframe}
        onClose={() => setShowIframe(false)}
        url={product.external_link}
        title={`Beli Tiket: ${product.title}`}
      />
    </div>
  );
};

export default ProductDetail;